
import { NodeTypeConfig, NodeCategory, WorkflowNodeData } from '../types';
import { NodePropertyConfig, TabConfig } from './propertyPanelRegistry';
import { PropertyFieldConfig } from '../components/molecules/PropertyField';

// --- Interfaces ---

export interface ExtensionHandler {
    name: string;           // e.g. 'PSS.RestTask'
    fields: string[];       // data keys this handler owns (excluded from Editor.Properties)
    create: (data: WorkflowNodeData) => any;
    parse: (ext: any, data: WorkflowNodeData) => void;
}

export interface NodeRegistration {
    nodeType: NodeTypeConfig;
    properties: NodePropertyConfig;
    engineType?: string;                    // e.g. 'ServiceTask' (omit = same as type key)
    defaultData?: Partial<WorkflowNodeData>;
    extension?: ExtensionHandler;
}

// --- Registry ---

const registry: Record<string, NodeRegistration> = {};

// Cached accessor results (invalidated when registry changes)
let cachedNodeTypes: Record<string, NodeTypeConfig> | null = null;
let cachedPropertyRegistry: Record<string, NodePropertyConfig> | null = null;

function invalidateCache() {
    cachedNodeTypes = null;
    cachedPropertyRegistry = null;
}

export function registerNode(key: string, config: NodeRegistration) {
    registry[key] = config;
    invalidateCache();
}

export function registerNodes(nodes: Record<string, NodeRegistration>) {
    Object.entries(nodes).forEach(([key, config]) => {
        registry[key] = config;
    });
    invalidateCache();
}

// --- Accessor Helpers ---

export function getNodeTypes(): Record<string, NodeTypeConfig> {
    if (cachedNodeTypes) return cachedNodeTypes;
    const result: Record<string, NodeTypeConfig> = {};
    for (const [key, reg] of Object.entries(registry)) {
        result[key] = reg.nodeType;
    }
    cachedNodeTypes = result;
    return result;
}

export function getPropertyRegistry(): Record<string, NodePropertyConfig> {
    if (cachedPropertyRegistry) return cachedPropertyRegistry;
    const result: Record<string, NodePropertyConfig> = {};
    for (const [key, reg] of Object.entries(registry)) {
        result[key] = reg.properties;
    }
    cachedPropertyRegistry = result;
    return result;
}

export function getEngineType(editorType: string): string {
    const reg = registry[editorType];
    if (reg?.engineType) return reg.engineType;
    return editorType;
}

export function getExtensionHandler(editorType: string): ExtensionHandler | undefined {
    return registry[editorType]?.extension;
}

export function getDefaultData(editorType: string): Partial<WorkflowNodeData> | undefined {
    return registry[editorType]?.defaultData;
}

/**
 * Resolve engine type + extensions back to editor type.
 * Used during load to determine which editor node type to use.
 */
export function resolveEditorType(engineType: string, extensions: any[]): string | null {
    for (const [key, reg] of Object.entries(registry)) {
        if (reg.extension && reg.engineType === engineType) {
            // Check if any extension matches this handler's name
            if (extensions?.some((e: any) => e.Name === reg.extension!.name)) {
                return key;
            }
        }
    }
    return null;
}

/**
 * Get all field names handled by a node type's extension (excluded from Editor.Properties).
 */
export function getHandledFields(editorType: string): string[] {
    return registry[editorType]?.extension?.fields || [];
}

/**
 * Get the raw registry (for advanced use cases).
 */
export function getRegistry(): Readonly<Record<string, NodeRegistration>> {
    return registry;
}

// --- Common field definitions shared across registrations ---

const commonFields: PropertyFieldConfig[] = [
    { name: 'label', label: 'Display Label', type: 'text', validation: { required: true } },
    { name: 'description', label: 'Description', type: 'textarea', rows: 2 },
];

const errorHandlingFields: PropertyFieldConfig[] = [
    {
        name: 'onException', label: 'On Exception', type: 'select',
        options: [
            { value: '0', label: 'Retry' },
            { value: '1', label: 'Ignore' },
            { value: '2', label: 'Throw' }
        ],
        defaultValue: '0',
        description: 'Behavior when the task throws an exception.'
    },
    {
        name: 'retries', label: 'Retries', type: 'number',
        defaultValue: 3,
        description: 'Number of retry attempts before failing.',
        condition: { field: 'onException', operator: 'equals', value: '0' }
    }
];

const serviceTaskCommonFields: PropertyFieldConfig[] = [
    ...commonFields,
    ...errorHandlingFields,
];

const boundaryFields: PropertyFieldConfig[] = [
    ...commonFields,
    {
        name: 'cancelActivity',
        label: 'Interrupting',
        type: 'boolean',
        description: 'Cancel the attached activity when triggered',
        defaultValue: true
    }
];

// --- Colors ---
const COLORS = {
    Start: '#059669',
    End: '#64748b',
    Error: '#dc2626',
    Task: '#0f766e',
    System: '#334155',
    Gateway: '#d97706',
    Event: '#2563eb',
    Sub: '#475569',
};

// --- Built-in Node Registrations ---

// Start Events
registerNode('BlankStartEvent', {
    nodeType: { type: 'BlankStartEvent', label: 'Start Event', color: COLORS.Start, icon: 'Play', category: NodeCategory.Start, description: 'Generic start point' },
    properties: { type: 'BlankStartEvent', tabs: [
        { id: 'properties', label: 'General', fields: commonFields },
        { id: 'startprocess', label: 'Start Config', fields: [], specialComponent: 'StartProcessTab' }
    ] },
});

registerNode('FormStartEvent', {
    nodeType: { type: 'FormStartEvent', label: 'Form Start', color: COLORS.Start, icon: 'FileJson', category: NodeCategory.Start, description: 'Manual process start with form' },
    properties: {
        type: 'FormStartEvent',
        tabs: [
            { id: 'properties', label: 'General', fields: [...commonFields, { name: 'formTitle', label: 'Form Title', type: 'text' }] },
            { id: 'form', label: 'Form Builder', fields: [], specialComponent: 'FormBuilderTab', layout: 'fullscreen' },
            { id: 'startprocess', label: 'Start Config', fields: [], specialComponent: 'StartProcessTab' }
        ]
    },
    engineType: 'BlankStartEvent',
    defaultData: { formSchema: { fields: [] }, formTitle: 'New Form' },
});

registerNode('TimerStartEvent', {
    nodeType: { type: 'TimerStartEvent', label: 'Timer Start', color: COLORS.Start, icon: 'Clock', category: NodeCategory.Start, description: 'Timer-triggered start' },
    properties: {
        type: 'TimerStartEvent',
        tabs: [
            { id: 'properties', label: 'General', fields: commonFields },
            { id: 'timer', label: 'Timer', fields: [], specialComponent: 'TimerTab' },
            { id: 'startprocess', label: 'Start Config', fields: [], specialComponent: 'StartProcessTab' }
        ]
    },
});

registerNode('MessageStartEvent', {
    nodeType: { type: 'MessageStartEvent', label: 'Message Start', color: COLORS.Start, icon: 'Mail', category: NodeCategory.Start, description: 'Message-triggered start' },
    properties: {
        type: 'MessageStartEvent',
        tabs: [
            { id: 'properties', label: 'General', fields: commonFields },
            { id: 'message', label: 'Message', fields: [], specialComponent: 'MessageTab' },
            { id: 'startprocess', label: 'Start Config', fields: [], specialComponent: 'StartProcessTab' }
        ]
    },
});

registerNode('SignalStartEvent', {
    nodeType: { type: 'SignalStartEvent', label: 'Signal Start', color: COLORS.Start, icon: 'Zap', category: NodeCategory.Start, description: 'Signal-triggered start' },
    properties: {
        type: 'SignalStartEvent',
        tabs: [
            { id: 'properties', label: 'General', fields: commonFields },
            { id: 'signal', label: 'Signal', fields: [], specialComponent: 'SignalTab' },
            { id: 'startprocess', label: 'Start Config', fields: [], specialComponent: 'StartProcessTab' }
        ]
    },
});

// End Events
registerNode('BlankEndEvent', {
    nodeType: { type: 'BlankEndEvent', label: 'End', color: COLORS.End, icon: 'Square', category: NodeCategory.End, description: 'Process completion' },
    properties: { type: 'BlankEndEvent', tabs: [{ id: 'properties', label: 'General', fields: commonFields }] },
});

registerNode('ErrorEndEvent', {
    nodeType: { type: 'ErrorEndEvent', label: 'Error End', color: COLORS.Error, icon: 'AlertTriangle', category: NodeCategory.End, description: 'Error termination' },
    properties: {
        type: 'ErrorEndEvent',
        tabs: [
            { id: 'properties', label: 'General', fields: commonFields },
            {
                id: 'error', label: 'Error Config', fields: [
                    { name: 'errorMessage', label: 'Error Message', type: 'text', placeholder: 'e.g. Payment Failed', description: 'A human-readable message for the error.' },
                    { name: 'errorPayload', label: 'Error Payload (Script)', type: 'expression', rows: 6, placeholder: '{\n  code: 500,\n  reason: variables.reason\n}', description: 'Expression returning error data.' }
                ]
            }
        ]
    },
});

registerNode('MessageEndEvent', {
    nodeType: { type: 'MessageEndEvent', label: 'Message End', color: COLORS.End, icon: 'Upload', category: NodeCategory.End, description: 'Message termination' },
    properties: { type: 'MessageEndEvent', tabs: [{ id: 'properties', label: 'General', fields: commonFields }, { id: 'message', label: 'Message', fields: [], specialComponent: 'MessageThrowTab' }] },
});

registerNode('SignalEndEvent', {
    nodeType: { type: 'SignalEndEvent', label: 'Signal End', color: COLORS.End, icon: 'Radio', category: NodeCategory.End, description: 'Signal termination' },
    properties: { type: 'SignalEndEvent', tabs: [{ id: 'properties', label: 'General', fields: commonFields }, { id: 'signal', label: 'Signal', fields: [], specialComponent: 'SignalThrowTab' }] },
});

registerNode('TerminateEndEvent', {
    nodeType: { type: 'TerminateEndEvent', label: 'Terminate End', color: COLORS.Error, icon: 'StopCircle', category: NodeCategory.End, description: 'Terminate all instances' },
    properties: { type: 'TerminateEndEvent', tabs: [{ id: 'properties', label: 'General', fields: commonFields }] },
});

// Tasks
registerNode('UserTask', {
    nodeType: { type: 'UserTask', label: 'User Task', color: COLORS.Task, icon: 'User', category: NodeCategory.Task, description: 'Requires human interaction' },
    properties: {
        type: 'UserTask',
        tabs: [
            { id: 'properties', label: 'General', fields: [...serviceTaskCommonFields, { name: 'userTaskType', label: 'Task Type', type: 'select', options: [{ value: 'generic', label: 'Generic' }, { value: 'produce', label: 'Badge Production' }, { value: 'smartbridge', label: 'SmartBridge' }, { value: 'receipt', label: 'Receipt View' }] }] },
            { id: 'form', label: 'Form Builder', fields: [], specialComponent: 'FormBuilderTab', layout: 'fullscreen' },
            { id: 'assignments', label: 'Assignments', fields: [], specialComponent: 'AssignmentsTab' },
            { id: 'extensions', label: 'Extensions', fields: [], specialComponent: 'UserTaskExtensionTab', layout: 'fullscreen' }
        ]
    },
    engineType: 'UserTask',
    defaultData: { formSchema: { fields: [] }, formTitle: 'New Form' },
    extension: {
        name: 'PSS.GenericUserTask',
        fields: ['userTaskType', 'assignments', 'extensionConfig', 'extensionRelations', 'productionProcessKey', 'productionOperations', 'jobScript', 'assignee', 'priority', 'onException', 'retries'],
        create: () => null, // UserTask uses special handling - set in pssHelper
        parse: () => {},    // UserTask uses special handling - set in pssHelper
    },
});

registerNode('ScriptTask', {
    nodeType: { type: 'ScriptTask', label: 'Script Task', color: COLORS.System, icon: 'FileJson', category: NodeCategory.Task, description: 'Executes custom code' },
    properties: {
        type: 'ScriptTask',
        tabs: [
            { id: 'properties', label: 'General', fields: commonFields },
            { id: 'script', label: 'Script', fields: [], specialComponent: 'ScriptTab', layout: 'fullscreen' }
        ]
    },
});

registerNode('ServiceTaskREST', {
    nodeType: { type: 'ServiceTaskREST', label: 'REST Service', color: COLORS.System, icon: 'Globe', category: NodeCategory.Task, description: 'REST API integration' },
    properties: {
        type: 'ServiceTaskREST',
        tabs: [
            { id: 'properties', label: 'General', fields: serviceTaskCommonFields },
            { id: 'http', label: 'HTTP Config', fields: [], specialComponent: 'RestConfigTab', layout: 'fullscreen' },
            { id: 'extraction', label: 'Extraction', fields: [], specialComponent: 'ExtractionTab', layout: 'fullscreen' }
        ]
    },
    engineType: 'ServiceTask',
    defaultData: { method: 'GET', resultVariable: 'apiResult', headers: { 'Content-Type': 'application/json' } },
    extension: {
        name: 'PSS.RestTask',
        fields: ['restTopic', 'endpoint', 'method', 'requestBody', 'headers', 'authentication', 'bearerToken', 'apiKey', 'onException', 'retries'],
        create: () => null, // Actual functions stay in pssHelper
        parse: () => {},
    },
});

registerNode('ServiceTaskLLM', {
    nodeType: { type: 'ServiceTaskLLM', label: 'LLM Service', color: COLORS.System, icon: 'Bot', category: NodeCategory.Task, description: 'AI/LLM integration' },
    properties: {
        type: 'ServiceTaskLLM',
        tabs: [
            { id: 'properties', label: 'General', fields: serviceTaskCommonFields },
            { id: 'llm', label: 'LLM Config', fields: [], specialComponent: 'LLMConfigTab', layout: 'fullscreen' },
            { id: 'extraction', label: 'Extraction', fields: [], specialComponent: 'ExtractionTab', layout: 'fullscreen' }
        ]
    },
    engineType: 'ServiceTask',
    extension: {
        name: 'PSS.AgentTask',
        fields: ['model', 'systemPrompt', 'userPrompt', 'outputVariable', 'onException', 'retries'],
        create: () => null,
        parse: () => {},
    },
});

registerNode('ServiceTaskRules', {
    nodeType: { type: 'ServiceTaskRules', label: 'Rules Task', color: COLORS.System, icon: 'Scale', category: NodeCategory.Task, description: 'Execute DMN rules' },
    properties: {
        type: 'ServiceTaskRules',
        tabs: [
            { id: 'properties', label: 'General', fields: serviceTaskCommonFields },
            { id: 'decision', label: 'Decision Config', fields: [], specialComponent: 'DecisionConfigTab', layout: 'fullscreen' },
            { id: 'extraction', label: 'Extraction', fields: [], specialComponent: 'ExtractionTab', layout: 'fullscreen' }
        ]
    },
    engineType: 'ServiceTask',
    extension: {
        name: 'PSS.DecisionTask',
        fields: ['decisionFile', 'inputMapping', '_outputSchema', 'onException', 'retries'],
        create: () => null,
        parse: () => {},
    },
});

registerNode('ServiceTaskEmail', {
    nodeType: { type: 'ServiceTaskEmail', label: 'Email Service', color: COLORS.System, icon: 'Mail', category: NodeCategory.Task, description: 'Email sending' },
    properties: {
        type: 'ServiceTaskEmail',
        tabs: [
            { id: 'properties', label: 'General', fields: serviceTaskCommonFields },
            { id: 'email', label: 'Email Template', fields: [], specialComponent: 'EmailTemplateTab', layout: 'fullscreen' }
        ]
    },
    engineType: 'ServiceTask',
    extension: {
        name: 'PSS.EmailTask',
        fields: ['fromEmail', 'fromName', 'to', 'toNames', 'cc', 'ccNames', 'bcc', 'bccNames', 'subject', 'body', 'isHtml', 'attachments', 'onException', 'retries'],
        create: () => null,
        parse: () => {},
    },
});

registerNode('ServiceTaskSMS', {
    nodeType: { type: 'ServiceTaskSMS', label: 'SMS Service', color: COLORS.System, icon: 'Smartphone', category: NodeCategory.Task, description: 'Send text messages' },
    properties: {
        type: 'ServiceTaskSMS',
        tabs: [
            { id: 'properties', label: 'General', fields: serviceTaskCommonFields },
            { id: 'sms', label: 'SMS Config', fields: [], specialComponent: 'SmsTab', layout: 'fullscreen' }
        ]
    },
    engineType: 'ServiceTask',
    extension: {
        name: 'PSS.SmsTask',
        fields: ['toPhone', 'message', 'onException', 'retries'],
        create: () => null,
        parse: () => {},
    },
});

registerNode('ServiceTaskDB', {
    nodeType: { type: 'ServiceTaskDB', label: 'Database Service', color: COLORS.System, icon: 'Database', category: NodeCategory.Task, description: 'Database operations' },
    properties: {
        type: 'ServiceTaskDB',
        tabs: [
            { id: 'properties', label: 'General', fields: serviceTaskCommonFields },
            { id: 'database', label: 'Database', fields: [], specialComponent: 'DatabaseTab', layout: 'fullscreen' },
            { id: 'extraction', label: 'Extraction', fields: [], specialComponent: 'ExtractionTab', layout: 'fullscreen' }
        ]
    },
    engineType: 'ServiceTask',
    extension: {
        name: 'PSS.DatabaseTask',
        fields: ['connectionId', 'queryType', 'query', 'timeout', 'parameters', 'onException', 'retries'],
        create: () => null,
        parse: () => {},
    },
});

registerNode('ServiceTaskMCP', {
    nodeType: { type: 'ServiceTaskMCP', label: 'MCP Service', color: COLORS.System, icon: 'Server', category: NodeCategory.Task, description: 'Model Context Protocol client' },
    properties: {
        type: 'ServiceTaskMCP',
        tabs: [
            { id: 'properties', label: 'General', fields: serviceTaskCommonFields },
            { id: 'mcp', label: 'MCP Config', fields: [], specialComponent: 'McpConfigTab', layout: 'fullscreen' },
            { id: 'extraction', label: 'Extraction', fields: [], specialComponent: 'ExtractionTab', layout: 'fullscreen' }
        ]
    },
    engineType: 'ServiceTask',
    extension: {
        name: 'PSS.McpTask',
        fields: ['mcpServerUrl', 'mcpServerName', 'mcpOperation', 'mcpToolName', 'mcpToolArguments', 'mcpResourceUri', 'mcpPromptName', 'mcpPromptArguments', 'onException', 'retries'],
        create: () => null,
        parse: () => {},
    },
});

// Gateways
registerNode('XorGateway', {
    nodeType: { type: 'XorGateway', label: 'Exclusive Gateway', color: COLORS.Gateway, icon: 'GitFork', category: NodeCategory.Gateway, description: 'Single path selection' },
    properties: {
        type: 'XorGateway',
        tabs: [
            { id: 'properties', label: 'General', fields: [...commonFields, { name: 'isMerging', label: 'Is Merging', type: 'boolean' }] },
            { id: 'routing', label: 'Routing', fields: [], specialComponent: 'ConditionsTab', layout: 'fullscreen' }
        ]
    },
    defaultData: { outputs: ['default'], conditions: [{ path: 0, label: 'default', expression: '', description: 'Default path' }], evaluationOrder: 'sequential' },
});

registerNode('ParallelGateway', {
    nodeType: { type: 'ParallelGateway', label: 'Parallel Gateway', color: COLORS.Gateway, icon: 'Plus', category: NodeCategory.Gateway, description: 'Parallel execution' },
    properties: {
        type: 'ParallelGateway',
        tabs: [
            { id: 'properties', label: 'General', fields: [...commonFields, { name: 'isMerging', label: 'Is Merging', type: 'boolean' }] },
            { id: 'routing', label: 'Config', fields: [], specialComponent: 'ConditionsTab', layout: 'fullscreen' }
        ]
    },
});

registerNode('InclusiveGateway', {
    nodeType: { type: 'InclusiveGateway', label: 'Inclusive Gateway', color: COLORS.Gateway, icon: 'Circle', category: NodeCategory.Gateway, description: 'Multi-path routing' },
    properties: {
        type: 'InclusiveGateway',
        tabs: [
            { id: 'properties', label: 'General', fields: [...commonFields, { name: 'isMerging', label: 'Is Merging', type: 'boolean' }] },
            { id: 'routing', label: 'Routing', fields: [], specialComponent: 'ConditionsTab', layout: 'fullscreen' }
        ]
    },
    defaultData: { outputs: ['default'], conditions: [{ path: 0, label: 'default', expression: '', description: 'Default path' }], evaluationOrder: 'parallel' },
});

registerNode('EventBasedGateway', {
    nodeType: { type: 'EventBasedGateway', label: 'Event Gateway', color: COLORS.Gateway, icon: 'Router', category: NodeCategory.Gateway, description: 'Event-driven routing', disabled: true },
    properties: { type: 'EventBasedGateway', tabs: [{ id: 'properties', label: 'General', fields: commonFields }] },
});

// Activities
registerNode('SubProcess', {
    nodeType: { type: 'SubProcess', label: 'Sub Process', color: COLORS.Sub, icon: 'Layers', category: NodeCategory.Activity, description: 'Embedded subprocess' },
    properties: { type: 'SubProcess', tabs: [{ id: 'properties', label: 'General', fields: [
        ...commonFields,
        { name: 'resultVariable', label: 'Result Variable', type: 'text', placeholder: 'subProcessResult', description: 'Variable to store the returned values from the subprocess as an object.' },
    ] }] },
});

registerNode('CallActivity', {
    nodeType: { type: 'CallActivity', label: 'Call Activity', color: COLORS.Sub, icon: 'ExternalLink', category: NodeCategory.Activity, description: 'Call external process' },
    properties: {
        type: 'CallActivity',
        tabs: [
            { id: 'properties', label: 'General', fields: commonFields },
            { id: 'call', label: 'Process Call', fields: [], specialComponent: 'CallActivityTab', layout: 'fullscreen' }
        ]
    },
    engineType: 'CallActivity',
});

// Intermediate Events
registerNode('IntermediateCatchMessageEvent', {
    nodeType: { type: 'IntermediateCatchMessageEvent', label: 'Catch Message', color: COLORS.Event, icon: 'Mail', category: NodeCategory.Intermediate, description: 'Waits for message' },
    properties: { type: 'IntermediateCatchMessageEvent', tabs: [{ id: 'properties', label: 'General', fields: commonFields }, { id: 'message', label: 'Message', fields: [], specialComponent: 'MessageTab' }] },
});

registerNode('IntermediateCatchSignalEvent', {
    nodeType: { type: 'IntermediateCatchSignalEvent', label: 'Catch Signal', color: COLORS.Event, icon: 'Zap', category: NodeCategory.Intermediate, description: 'Waits for signal' },
    properties: { type: 'IntermediateCatchSignalEvent', tabs: [{ id: 'properties', label: 'General', fields: commonFields }, { id: 'signal', label: 'Signal', fields: [], specialComponent: 'SignalTab' }] },
});

registerNode('IntermediateCatchTimerEvent', {
    nodeType: { type: 'IntermediateCatchTimerEvent', label: 'Catch Timer', color: COLORS.Event, icon: 'Clock', category: NodeCategory.Intermediate, description: 'Waits for timer' },
    properties: { type: 'IntermediateCatchTimerEvent', tabs: [{ id: 'properties', label: 'General', fields: commonFields }, { id: 'timer', label: 'Timer', fields: [], specialComponent: 'TimerTab' }] },
});

registerNode('IntermediateThrowMessageEvent', {
    nodeType: { type: 'IntermediateThrowMessageEvent', label: 'Throw Message', color: COLORS.Event, icon: 'Send', category: NodeCategory.Intermediate, description: 'Sends message' },
    properties: { type: 'IntermediateThrowMessageEvent', tabs: [{ id: 'properties', label: 'General', fields: commonFields }, { id: 'message', label: 'Message', fields: [], specialComponent: 'MessageThrowTab' }] },
});

registerNode('IntermediateThrowSignalEvent', {
    nodeType: { type: 'IntermediateThrowSignalEvent', label: 'Throw Signal', color: COLORS.Event, icon: 'Radio', category: NodeCategory.Intermediate, description: 'Throws signal' },
    properties: { type: 'IntermediateThrowSignalEvent', tabs: [{ id: 'properties', label: 'General', fields: commonFields }, { id: 'signal', label: 'Signal', fields: [], specialComponent: 'SignalThrowTab' }] },
});

// Boundary Events
registerNode('ErrorBoundaryEvent', {
    nodeType: { type: 'ErrorBoundaryEvent', label: 'Error Boundary', color: COLORS.Error, icon: 'AlertTriangle', category: NodeCategory.Boundary, description: 'Handles errors' },
    properties: {
        type: 'ErrorBoundaryEvent',
        tabs: [{
            id: 'properties', label: 'General', fields: [
                ...boundaryFields,
                { name: 'exceptionType', label: 'Error Name (Catch-all if empty)', type: 'text', placeholder: 'e.g. ValidationError', description: 'Leave empty to catch all errors.' }
            ]
        }]
    },
});

registerNode('MessageBoundaryEvent', {
    nodeType: { type: 'MessageBoundaryEvent', label: 'Message Boundary', color: COLORS.Event, icon: 'Mail', category: NodeCategory.Boundary, description: 'Handles messages' },
    properties: { type: 'MessageBoundaryEvent', tabs: [{ id: 'properties', label: 'General', fields: boundaryFields }, { id: 'message', label: 'Message', fields: [], specialComponent: 'MessageTab' }] },
});

registerNode('SignalBoundaryEvent', {
    nodeType: { type: 'SignalBoundaryEvent', label: 'Signal Boundary', color: COLORS.Event, icon: 'Zap', category: NodeCategory.Boundary, description: 'Handles signals' },
    properties: { type: 'SignalBoundaryEvent', tabs: [{ id: 'properties', label: 'General', fields: boundaryFields }, { id: 'signal', label: 'Signal', fields: [], specialComponent: 'SignalTab' }] },
});

registerNode('TimerBoundaryEvent', {
    nodeType: { type: 'TimerBoundaryEvent', label: 'Timer Boundary', color: COLORS.Event, icon: 'Clock', category: NodeCategory.Boundary, description: 'Handles timeouts' },
    properties: { type: 'TimerBoundaryEvent', tabs: [{ id: 'properties', label: 'General', fields: boundaryFields }, { id: 'timer', label: 'Timer', fields: [], specialComponent: 'TimerTab' }] },
});
